import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminLogin() {
  const { login, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (isAdmin) navigate("/admin", { replace: true });
  }, [isAdmin, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return toast.error("Enter the admin password");
    setSubmitting(true);
    try {
      await login(password);
      toast.success("Welcome back, admin");
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF6EF] flex flex-col" data-testid="admin-login-page">
      <div className="max-w-md mx-auto w-full px-5 pt-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-brand-700 hover:text-brand-900"
          data-testid="login-back-link"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.6} />
          Back to order entry
        </Link>
      </div>
      <main className="max-w-md mx-auto w-full px-5 pt-10 pb-16 flex-1">
        <div className="mb-8 flex flex-col items-center">
          <div className="w-16 h-16 rounded-3xl bg-brand-700 flex items-center justify-center shadow-md mb-4">
            <Lock className="w-7 h-7 text-white" strokeWidth={1.8} />
          </div>
          <h1 className="font-heading text-3xl font-bold text-brand-900 tracking-tight text-center">
            Admin sign in
          </h1>
          <p className="text-sm text-brand-700/70 mt-1 text-center max-w-xs">
            Enter the password to manage fabrics, products and thresholds.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.18em] text-brand-700/70 font-medium">
              Password
            </Label>
            <Input
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="h-14 rounded-2xl bg-white border-brand-200 px-5 text-base"
              data-testid="admin-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-14 rounded-2xl bg-brand-700 hover:bg-brand-900 text-white text-base font-medium active:scale-[0.98]"
            data-testid="admin-login-btn"
          >
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </main>
    </div>
  );
}
